import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { pushSupported, subscribeToPush, unsubscribeFromPush, ensureSWRegistered } from "@/lib/push";

function canShowSystemNotif() {
  return typeof window !== "undefined" && "Notification" in window;
}

function showSystem(title, body) {
  if (!canShowSystemNotif()) return;
  if (Notification.permission !== "granted") return;
  try { new Notification(title, { body, icon: "/favicon.ico", tag: title + body }); } catch {}
}

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [perm, setPerm] = useState(canShowSystemNotif() ? Notification.permission : "denied");
  const [pushOn, setPushOn] = useState(false);
  const seenIds = React.useRef(new Set());
  const isFirstFetch = React.useRef(true);

  useEffect(() => {
    (async () => {
      if (!pushSupported()) return;
      try {
        const reg = await ensureSWRegistered();
        const sub = reg ? await reg.pushManager.getSubscription() : null;
        setPushOn(!!sub);
      } catch {}
    })();
  }, []);

  const fetch = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      data.forEach((n) => {
        if (!seenIds.current.has(n.notif_id)) {
          seenIds.current.add(n.notif_id);
          if (!n.read && !isFirstFetch.current) {
            toast(n.title, { description: n.message });
            // Avoid duplicate when SW push already showed
            if (!pushOn) showSystem(n.title, n.message);
          }
        }
      });
      isFirstFetch.current = false;
      setItems(data);
    } catch {}
  }, [pushOn]);

  useEffect(() => {
    fetch();
    const t = setInterval(fetch, 30000);
    return () => clearInterval(t);
  }, [fetch]);

  const unread = items.filter((n) => !n.read).length;

  const markAllRead = async () => {
    try { await api.post("/notifications/read-all"); fetch(); } catch {}
  };

  const enablePush = async () => {
    if (!pushSupported()) { toast.error("Push non supporté par ce navigateur"); return; }
    try {
      const res = await subscribeToPush();
      if (res.ok) {
        setPerm("granted");
        setPushOn(true);
        toast.success("Notifications push activées");
        try { await api.post("/push/test"); } catch {}
      } else if (res.reason === "denied") {
        toast.error("Permission refusée. Activez les notifications dans les réglages du navigateur.");
      } else {
        toast.error("Activation impossible");
      }
    } catch (e) {
      toast.error("Échec de l'abonnement push");
    }
  };

  const disablePush = async () => {
    try { await unsubscribeFromPush(); setPushOn(false); toast.success("Push désactivé"); }
    catch { toast.error("Échec de la désinscription"); }
  };

  return (
    <div className="relative">
      <button
        data-testid="notification-bell-btn"
        onClick={() => setOpen((o) => !o)}
        className="relative border-2 border-black bg-white px-3 py-2 font-mono-eyebrow"
      >
        🔔
        {unread > 0 && (
          <span data-testid="notification-count" className="absolute -top-2 -right-2 bg-[#FFD600] border-2 border-black text-[10px] px-1.5 font-display">{unread}</span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-80 brut-card bg-white z-50" data-testid="notification-panel">
          <div className="p-3 border-b-2 border-black flex justify-between items-center">
            <span className="font-display text-lg">NOTIFICATIONS</span>
            {unread > 0 && (
              <button data-testid="mark-all-read-btn" onClick={markAllRead} className="font-mono-eyebrow text-xs underline">Tout lire</button>
            )}
          </div>
          {pushSupported() && (
            <div className="p-3 border-b-2 border-black bg-[#FFFCEA] flex items-center justify-between gap-2">
              <div>
                <p className="font-mono-eyebrow text-[10px]">// Push navigateur</p>
                <p className="text-xs mt-1">{pushOn ? "Activé — alertes en arrière-plan." : "Recevez les alertes même tab fermé."}</p>
              </div>
              {pushOn ? (
                <button data-testid="disable-push-btn" onClick={disablePush} className="brut-btn brut-btn-secondary text-xs px-3 py-1">Désactiver</button>
              ) : (
                <button data-testid="enable-push-btn" onClick={enablePush} className="brut-btn text-xs px-3 py-1">Activer →</button>
              )}
            </div>
          )}
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <p className="p-4 text-sm text-[#52525B]">Aucune notification</p>
            ) : items.map((n) => (
              <div key={n.notif_id} className={`p-3 border-b-2 border-black ${!n.read ? "bg-[#FFFCEA]" : "bg-white"}`} data-testid={`notif-${n.notif_id}`}>
                <p className="font-display text-sm">{n.title}</p>
                <p className="text-xs mt-1 text-[#52525B]">{n.message}</p>
                <p className="text-[10px] font-mono-eyebrow mt-1">{new Date(n.created_at).toLocaleString("fr-CH")}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
